import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Typography, Tag, Space, Input, message, Select, Tabs, Popconfirm } from 'antd';
import { SearchOutlined, EyeOutlined, EditOutlined, WarningOutlined, FilePdfOutlined, DeleteOutlined, BookOutlined, SafetyCertificateOutlined, FileProtectOutlined, SnippetsOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ListaEstanqueidade from '../components/ListaEstanqueidade';
import ListaDiversos from '../components/ListaDiversos';
import dayjs from 'dayjs';

// ordem padrao da lista: ultimos criados primeiro (nao pela data de modificacao)
const _quando = (x) => String(x.criado_em || x.created_at || x.data_criacao || x.data_inspecao || '');
const ordemCriacao = (a, b) => _quando(b).localeCompare(_quando(a)) || (Number(b.id) || 0) - (Number(a.id) || 0);

// padrao: rascunhos primeiro; dentro de cada grupo, o ultimo codigo criado em cima (sem codigo = recem-criado, vai no topo)
const _rasc = (x) => (String(x.status || '').toLowerCase() === 'rascunho' ? 0 : 1);
const _codn = (x) => { const m = String(x.codigo || x.codigo_cal || '').match(/(\d+)\s*$/) || String(x.codigo || x.codigo_cal || '').match(/(\d+)/); return m ? parseInt(m[1], 10) : 1e15; };
const ordemCodigo = (a, b) => (_rasc(a) - _rasc(b)) || (_codn(b) - _codn(a)) || ((Number(b.id) || 0) - (Number(a.id) || 0));

const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_CORES = { em_elaboracao: 'blue', concluido: 'green', cancelado: 'red' };
const STATUS_LABELS = { em_elaboracao: 'Em Elaboração', concluido: 'Concluído', cancelado: 'Cancelado' };

const RESULTADO_CORES = { apto: 'success', apto_com_observacoes: 'warning', reprovado: 'error' };
const RESULTADO_LABELS = { apto: 'Apto', apto_com_observacoes: 'Apto c/ Obs.', reprovado: 'Reprovado' };

function Laudos() {
  const [laudos, setLaudos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  // Ordenacao unica para TODAS as guias. Padrao: ultima modificacao, do mais
  // recente para o mais antigo. Comparo por tempo, nao por texto: parte das
  // datas chega como ISO e parte como Date, e comparar string misturaria.
  const [ordem, setOrdem] = useState('cod_desc');
  const ts = (v) => { const t = Date.parse(v); return isNaN(t) ? 0 : t; };
  const ordenar = (lista) => {
    const l = [...(lista || [])];
    if (ordem === 'cod_desc') return l.sort(ordemCodigo);
    if (ordem === 'insp_asc')
      return l.sort((a, b) => (ts(a.data_inspecao) || Infinity)
        - (ts(b.data_inspecao) || Infinity));
    if (ordem === 'insp_desc')
      return l.sort((a, b) => ts(b.data_inspecao) - ts(a.data_inspecao));
    const mod = (r) => ts(r.atualizado_em) || ts(r.criado_em) || ts(r.data_inspecao);
    return l.sort((a, b) => mod(b) - mod(a));
  };
  const [filtroCliente, setFiltroCliente] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState(null);
  const [selecionados, setSelecionados] = useState([]);
  const [calibracoes, setCalbracoes] = useState([]);
  const [loadingCal, setLoadingCal] = useState(false);
  const [buscaCal, setBuscaCal] = useState('');
  const [ensaiosLP, setEnsaiosLP] = useState([]);
  const [espessurasEND, setEspessurasEND] = useState([]);
  const [autoclaves, setAutoclaves] = useState([]);
  const [loadingLP, setLoadingLP] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    carregarLaudos();
    carregarClientes();
    carregarCalibracoes();
    carregarEnsaiosLP();
    carregarAutoclaves();
  }, []);

  const carregarLaudos = async () => {
    try {
      setLoading(true);
      const [r, re, rt, rq, rme, rth, rcam] = await Promise.all([
        api.get('/laudos'),
        api.get('/inspecoes-externas').catch(() => ({ data: [] })),
        api.get('/inspecoes-tubulacao').catch(() => ({ data: [] })),
        api.get('/inspecoes-tanque').catch(() => ({ data: [] })),
        api.get('/inspecoes-espessura').catch(() => ({ data: [] })),
        api.get('/testes-hidrostaticos').catch(() => ({ data: [] })),
        api.get('/inspecoes-camara').catch(() => ({ data: [] }))
      ]);
      // Inspecao externa e laudo de inspecao NR-13 como qualquer outro: entra
      // nesta lista, nao em guia separada. So a origem muda para onde os
      // botoes levam.
      const externas = (re.data || []).map((x) => ({
        ...x, origem: 'externa',
        tipo_laudo: x.tipo_laudo || 'Laudo de Inspeção Externa NR-13'
      }));
      const tubulacoes = (rt.data || []).map((x) => ({
        ...x, origem: 'tubulacao',
        tipo_laudo: x.tipo_laudo || 'Inspeção NR13 - Tubulação'
      }));
      const tanques = (rq.data || []).map((x) => ({
        ...x, origem: 'tanque',
        tipo_laudo: x.tipo_laudo || 'Inspeção NR13 - Tanque'
      }));
      const espessuras = (rme.data || []).map((x) => ({
        ...x, origem: 'espessura',
        tipo_laudo: x.tipo_laudo || 'Medição de Espessura'
      }));
      setEspessurasEND(espessuras);
      const hidrostaticos = (rth.data || []).map((x) => ({
        ...x, origem: 'hidrostatico',
        tipo_laudo: x.tipo_laudo || 'Teste Hidrostático'
      }));
      const camaras = (rcam.data || []).map((x) => ({
        ...x, origem: 'camara',
        tipo_laudo: x.tipo_laudo || 'Inspeção NR13 - Câmara de Esterilização'
      }));
      setLaudos([...(r.data || []), ...externas, ...tubulacoes, ...tanques, ...hidrostaticos, ...camaras].sort(ordemCriacao));
    } catch { message.error('Erro ao carregar laudos'); }
    finally { setLoading(false); }
  };

  const carregarClientes = async () => {
    try {
      const r = await api.get('/clientes');
      setClientes(r.data);
    } catch {}
  };

  const carregarEnsaiosLP = async () => {
    try {
      setLoadingLP(true);
      const r = await api.get('/ensaios-lp');
      setEnsaiosLP(r.data);
    } catch { /* guia vazia */ }
    finally { setLoadingLP(false); }
  };

  const carregarAutoclaves = async () => {
    try {
      const r = await api.get('/manutencao-autoclave');
      setAutoclaves(r.data);
    } catch { /* guia vazia */ }
  };

  const pdfExterna = async (r) => {
    message.loading({ content: 'Gerando PDF...', key: 'iext', duration: 0 });
    try {
      const resp = await api.get('/inspecoes-externas/' + r.id + '/pdf', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.destroy('iext');
    } catch (e) {
      message.destroy('iext');
      let msg = 'Erro ao gerar PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) {}
      message.error(msg);
    }
  };

  const excluirExterna = async (r) => {
    if (!window.confirm('Excluir a inspeção externa ' + (r.codigo || r.tag || '') + '?')) return;
    try {
      await api.delete('/inspecoes-externas/' + r.id);
      message.success('Laudo excluído!');
      carregarLaudos();
    } catch { message.error('Erro ao excluir'); }
  };

  const pdfTubulacao = async (r) => {
    message.loading({ content: 'Gerando PDF...', key: 'tub', duration: 0 });
    try {
      const resp = await api.get('/inspecoes-tubulacao/' + r.id + '/pdf', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.destroy('tub');
    } catch (e) {
      message.destroy('tub');
      let msg = 'Erro ao gerar PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) {}
      message.error(msg);
    }
  };

  const excluirTubulacao = async (r) => {
    if (!window.confirm('Excluir o laudo de tubulação ' + (r.codigo || r.tag || '') + '?')) return;
    try {
      await api.delete('/inspecoes-tubulacao/' + r.id);
      message.success('Laudo excluído!');
      carregarLaudos();
    } catch { message.error('Erro ao excluir'); }
  };

  const pdfHidrostatico = async (r) => {
    message.loading({ content: 'Gerando PDF...', key: 'th', duration: 0 });
    try {
      const resp = await api.get('/testes-hidrostaticos/' + r.id + '/pdf', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.destroy('th');
    } catch (e) {
      message.destroy('th');
      let msg = 'Erro ao gerar PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) {}
      message.error(msg);
    }
  };

  const excluirHidrostatico = async (r) => {
    if (!window.confirm('Excluir o laudo de teste hidrostático ' + (r.codigo || r.tag || '') + '?')) return;
    try {
      await api.delete('/testes-hidrostaticos/' + r.id);
      message.success('Laudo excluído!');
      carregarLaudos();
    } catch { message.error('Erro ao excluir'); }
  };

  const pdfEspessura = async (r) => {
    message.loading({ content: 'Gerando PDF...', key: 'me', duration: 0 });
    try {
      const resp = await api.get('/inspecoes-espessura/' + r.id + '/pdf', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.destroy('me');
    } catch (e) {
      message.destroy('me');
      let msg = 'Erro ao gerar PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) {}
      message.error(msg);
    }
  };

  const excluirEspessura = async (r) => {
    if (!window.confirm('Excluir o relatório de medição de espessura ' + (r.codigo || r.tag || '') + '?')) return;
    try {
      await api.delete('/inspecoes-espessura/' + r.id);
      message.success('Laudo excluído!');
      carregarLaudos();
    } catch { message.error('Erro ao excluir'); }
  };

  const pdfTanque = async (r) => {
    message.loading({ content: 'Gerando PDF...', key: 'tq', duration: 0 });
    try {
      const resp = await api.get('/inspecoes-tanque/' + r.id + '/pdf', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.destroy('tq');
    } catch (e) {
      message.destroy('tq');
      let msg = 'Erro ao gerar PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) {}
      message.error(msg);
    }
  };

  const pdfCamara = async (r) => {
    message.loading({ content: 'Gerando PDF...', key: 'tq', duration: 0 });
    try {
      const resp = await api.get('/inspecoes-camara/' + r.id + '/pdf', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.destroy('tq');
    } catch (e) {
      message.destroy('tq');
      let msg = 'Erro ao gerar PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) {}
      message.error(msg);
    }
  };

  const excluirTanque = async (r) => {
    if (!window.confirm('Excluir o laudo de tanque ' + (r.codigo || r.tag || '') + '?')) return;
    try {
      await api.delete('/inspecoes-tanque/' + r.id);
      message.success('Laudo excluído!');
      carregarLaudos();
    } catch { message.error('Erro ao excluir'); }
  };

  const excluirCamara = async (r) => {
    if (!window.confirm('Excluir o laudo de tanque ' + (r.codigo || r.tag || '') + '?')) return;
    try {
      await api.delete('/inspecoes-camara/' + r.id);
      message.success('Laudo excluído!');
      carregarLaudos();
    } catch { message.error('Erro ao excluir'); }
  };

  const pdfAutoclave = async (r) => {
    message.loading({ content: 'Gerando PDF...', key: 'mpa', duration: 0 });
    try {
      const resp = await api.get('/manutencao-autoclave/' + r.id + '/pdf', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.destroy('mpa');
      carregarAutoclaves();
    } catch { message.destroy('mpa'); message.error('Erro ao gerar PDF'); }
  };

  const pdfLP = async (r) => {
    message.loading({ content: 'Gerando PDF...', key: 'lp', duration: 0 });
    try {
      const resp = await api.get('/ensaios-lp/' + r.id + '/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      message.destroy('lp');
      carregarEnsaiosLP();
    } catch { message.destroy('lp'); message.error('Erro ao gerar PDF'); }
  };

  const excluirLP = async (r) => {
    try {
      await api.delete('/ensaios-lp/' + r.id);
      message.success('Ensaio excluído');
      carregarEnsaiosLP();
    } catch { message.error('Erro ao excluir'); }
  };

  const statusVencimento = (data) => {
    if (!data) return <Text type="secondary">-</Text>;
    const dias = dayjs(typeof data === 'string' ? data.substring(0,10) : data).diff(dayjs(), 'day');
    if (dias < 0) return <Tag color="red"><WarningOutlined /> Vencido</Tag>;
    if (dias <= 30) return <Tag color="orange"><WarningOutlined /> {dias}d</Tag>;
    if (dias <= 90) return <Tag color="gold">{dayjs(typeof data === 'string' ? data.substring(0,10) : data).format('DD/MM/YYYY')}</Tag>;
    return <Tag color="green">{dayjs(typeof data === 'string' ? data.substring(0,10) : data).format('DD/MM/YYYY')}</Tag>;
  };

  // ---- Botões comuns a todos os tipos de laudo (PDF guardado na HostGator) ----
  const verPdfGen = async (base, r) => {
    message.loading({ content: 'Carregando PDF...', key: 'vizg', duration: 0 });
    try {
      const resp = await api.get(base + '/' + r.id + '/pdf/visualizar', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.destroy('vizg');
    } catch (e) { message.destroy('vizg'); message.warning('PDF ainda não gerado. Use o botão vermelho para gerar.'); }
  };
  const gerarPdfGen = async (base, r) => {
    message.loading({ content: 'Gerando PDF...', key: 'pdfg', duration: 0 });
    try {
      let resp;
      try {
        resp = await api.get(base + '/' + r.id + '/pdf', { responseType: 'blob' });
      } catch (e) {
        if (e.response?.status !== 409) throw e;
        message.destroy('pdfg');
        if (!window.confirm('Este documento já está assinado digitalmente. Gerar um PDF novo APAGA a assinatura (será preciso assinar de novo). Continuar?')) return;
        message.loading({ content: 'Gerando PDF...', key: 'pdfg', duration: 0 });
        resp = await api.get(base + '/' + r.id + '/pdf?forcar=1', { responseType: 'blob' });
      }
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.success({ content: 'PDF gerado e salvo!', key: 'pdfg' });
    } catch (e) {
      message.destroy('pdfg');
      let msg = 'Erro ao gerar PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) {}
      message.error(msg);
    }
  };
  const assinarPdfGen = async (base, r, codigo) => {
    if (!window.confirm('Assinar digitalmente o documento ' + (codigo || '') + '? Este processo pode levar até 1 minuto.')) return;
    try {
      message.loading({ content: 'Assinando PDF com certificado A1...', key: 'signg', duration: 0 });
      await api.post(base + '/' + r.id + '/pdf/assinar', {}, { timeout: 120000 });
      message.success({ content: 'PDF assinado com sucesso!', key: 'signg' });
    } catch (e) { message.error({ content: 'Erro ao assinar: ' + (e.response?.data?.erro || e.message), key: 'signg' }); }
  };
  const botoesPdf = (base, r, codigo) => [
    <Button key="ver" icon={<EyeOutlined />} size="small" style={{color:'#1890ff',borderColor:'#1890ff'}} title="Visualizar PDF" onClick={() => verPdfGen(base, r)} />,
    <Button key="gerar" icon={<FilePdfOutlined />} size="small" style={{color:'#cf1322',borderColor:'#cf1322'}} title="Gerar novo PDF" onClick={() => gerarPdfGen(base, r)} />,
    <Button key="assinar" icon={<SafetyCertificateOutlined />} size="small" style={{color:'#52c41a',borderColor:'#52c41a'}} title="Assinar PDF" onClick={() => assinarPdfGen(base, r, codigo)} />,
  ];

  const visualizarPDF = async (laudo) => {
    try {
      message.loading({ content: 'Carregando PDF...', key: 'viz', duration: 0 });
      const response = await api.get('/laudos/' + laudo.id + '/pdf/visualizar', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      message.success({ content: 'PDF aberto!', key: 'viz' });
    } catch(e) {
      message.destroy('viz');
      message.warning('PDF não gerado ainda. Use o botão PDF para gerar.');
    }
  };

  const assinarPDF = async (laudo) => {
    if (!window.confirm('Assinar digitalmente o laudo ' + laudo.codigo + '? Este processo pode levar até 1 minuto.')) return;
    try {
      message.loading({ content: 'Assinando PDF com certificado A1...', key: 'sign', duration: 0 });
      await api.post('/laudos/' + laudo.id + '/pdf/assinar', {}, { timeout: 120000 });
      message.success({ content: 'PDF assinado com sucesso!', key: 'sign' });
    } catch(e) {
      message.error({ content: 'Erro ao assinar: ' + (e.response?.data?.erro || e.message), key: 'sign' });
    }
  };

  const gerarPDF = async (laudo) => {
    try {
      message.loading({ content: 'Gerando PDF...', key: 'pdf', duration: 0 });
      try {
        await api.get(`/laudos/${laudo.id}/pdf`, { responseType: 'blob' });
      } catch (e) {
        if (e.response?.status !== 409) throw e;
        message.destroy('pdf');
        if (!window.confirm('O laudo ' + laudo.codigo + ' já está assinado digitalmente. Gerar um PDF novo APAGA a assinatura (será preciso assinar de novo). Continuar?')) return;
        message.loading({ content: 'Gerando PDF...', key: 'pdf', duration: 0 });
        await api.get(`/laudos/${laudo.id}/pdf?forcar=1`, { responseType: 'blob' });
      }
      message.success({ content: 'PDF gerado e salvo!', key: 'pdf' });
    } catch (e) { message.error({ content: 'Erro ao gerar PDF: ' + (e.response?.data?.erro || e.message), key: 'pdf' }); }
  };

  const visualizarLivro = async (laudo) => { // v2
    try {
      message.loading({ content: 'Carregando Livro...', key: 'livro_ver', duration: 0 });
      const response = await api.get('/laudos/' + laudo.id + '/livro-registro/visualizar', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      message.destroy('livro_ver');
    } catch { message.error({ content: 'Livro não disponível. Gere primeiro.', key: 'livro_ver' }); }
  };

  const visualizarProntuario = async (laudo) => {
    try {
      message.loading({ content: 'Carregando Prontuário...', key: 'pront_ver', duration: 0 });
      const response = await api.get('/laudos/' + laudo.id + '/prontuario/visualizar', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      message.destroy('pront_ver');
    } catch { message.error({ content: 'Prontuário não disponível. Gere primeiro.', key: 'pront_ver' }); }
  };

  // ---- anotacao para o livro (todos os laudos) e livro da camara
  const gerarAnotacao = async (tipo, id) => {
    message.loading({ content: 'Gerando anotação...', key: 'anot', duration: 0 });
    try {
      const resp = await api.post('/anotacoes/' + tipo + '/' + id, {}, { responseType: 'blob', timeout: 120000 });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.success({ content: 'Anotação gerada e enviada ao portal do cliente!', key: 'anot' });
    } catch (e) {
      let msg = e.message;
      try { const t = await e.response.data.text(); msg = JSON.parse(t).erro || msg; } catch (x) { /* blob sem json */ }
      message.error({ content: msg, key: 'anot', duration: 6 });
    }
  };
  const verAnotacao = async (tipo, id) => {
    message.loading({ content: 'Abrindo anotação...', key: 'anotv', duration: 0 });
    try {
      const resp = await api.get('/anotacoes/' + tipo + '/' + id, { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.destroy('anotv');
    } catch { message.warning({ content: 'Anotação ainda não gerada. Use o botão laranja para gerar.', key: 'anotv' }); }
  };
  const botoesAnotacao = (tipo, id) => [
    <Button key="anv" icon={<SnippetsOutlined />} size="small" style={{color:'#d46b08',borderColor:'#d46b08'}} title="Ver anotação do livro" onClick={() => verAnotacao(tipo, id)} />,
    <Button key="ang" icon={<SnippetsOutlined />} size="small" style={{color:'#d46b08',borderColor:'#d46b08',background:'#fff7e6'}} title="Gerar anotação do livro (vai para o portal)" onClick={() => gerarAnotacao(tipo, id)} />,
  ];
  const livroCamara = async (r, gerar) => {
    message.loading({ content: gerar ? 'Gerando Livro...' : 'Carregando Livro...', key: 'livcm', duration: 0 });
    try {
      const resp = await api.get('/inspecoes-camara/' + r.id + '/livro-registro' + (gerar ? '' : '/visualizar'), { responseType: 'blob', timeout: 120000 });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.success({ content: gerar ? 'Livro gerado e salvo!' : 'Livro aberto', key: 'livcm' });
    } catch { message.error({ content: gerar ? 'Erro ao gerar livro (gere o PDF do laudo antes)' : 'Livro não disponível. Gere primeiro.', key: 'livcm' }); }
  };

  const gerarLivro = async (laudo) => {
    if (!window.confirm('Gerar o LIVRO DE REGISTRO do laudo ' + (laudo.codigo || '') + '? Ele passa a aparecer para o cliente.')) return;
    try {
      message.loading({ content: 'Gerando Livro...', key: 'livro', duration: 0 });
      await api.get(`/laudos/${laudo.id}/livro-registro`, { responseType: 'blob' });
      message.success({ content: 'Livro gerado e salvo com sucesso!', key: 'livro' });
    } catch { message.error({ content: 'Erro ao gerar livro', key: 'livro' }); }
  };

  const excluirLaudo = async (laudo) => {
    if (!window.confirm(`Excluir laudo ${laudo.codigo}?`)) return;
    try {
      await api.delete(`/laudos/${laudo.id}`);
      message.success('Laudo excluído!');
      carregarLaudos();
    } catch { message.error('Erro ao excluir laudo'); }
  };

  // a chave da linha leva a origem na frente (e3 = externa 3, t5 = tubulacao 5...)
  const ROTA_POR_PREFIXO = { e: '/inspecoes-externas/', t: '/inspecoes-tubulacao/', q: '/inspecoes-tanque/',
    m: '/inspecoes-espessura/', h: '/testes-hidrostaticos/', c: '/inspecoes-camara/' };
  const excluirSelecionados = async () => {
    if (!window.confirm(`Excluir ${selecionados.length} laudos selecionados? Não dá para desfazer.`)) return;
    let ok = 0, falhas = 0;
    for (const chave of selecionados) {
      const k = String(chave);
      const rota = ROTA_POR_PREFIXO[k[0]] ? ROTA_POR_PREFIXO[k[0]] + k.slice(1) : '/laudos/' + k;
      try { await api.delete(rota); ok++; } catch { falhas++; }
    }
    if (ok) message.success(`${ok} laudo(s) excluído(s)` + (falhas ? `; ${falhas} com erro` : ''));
    else message.error('Nenhum laudo foi excluído');
    setSelecionados([]);
    carregarLaudos();
  };

  const gerarProntuario = async (laudo) => {
    if (!window.confirm('Gerar o PRONTUÁRIO do laudo ' + (laudo.codigo || '') + '? Ele passa a aparecer para o cliente.')) return;
    try {
      message.loading({ content: 'Gerando Prontuário...', key: 'pront', duration: 0 });
      await api.get(`/laudos/${laudo.id}/prontuario`, { responseType: 'blob' });
      message.success({ content: 'Prontuário gerado e salvo com sucesso!', key: 'pront' });
    } catch(e) { message.error({ content: 'Erro ao gerar prontuário', key: 'pront' }); }
  };

  const gerarLivroDoc = async (laudo) => {
    if (!window.confirm('Gerar o LIVRO DE REGISTRO do laudo ' + (laudo.codigo || '') + '? Ele passa a aparecer para o cliente.')) return;
    try {
      message.loading({ content: 'Gerando Livro...', key: 'livro', duration: 0 });
      const response = await api.get('/laudos/' + laudo.id + '/livro-registro', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Livro-' + laudo.codigo + '.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success({ content: 'Livro gerado!', key: 'livro' });
    } catch { message.error({ content: 'Erro ao gerar livro', key: 'livro' }); }
  };

  const excluirCalibracao = async (r) => {
    try {
      if (r.origem === 'calibracao_avulsa') {
        await api.delete('/calibracoes/' + r.id);
      } else {
        const tipo = r.tipo === 'manometro' ? 'manometros' : 'valvulas';
        await api.delete('/instrumentos/' + r.laudo_id + '/' + tipo + '/' + r.id + '?origem=' + (r.origem || 'laudo'));
      }
      message.success('Calibração excluída');
      carregarCalibracoes();
    } catch { message.error('Erro ao excluir'); }
  };

  const carregarCalibracoes = async () => {
    try {
      setLoadingCal(true);
      const r = await api.get('/laudos/calibracoes');
      setCalbracoes(r.data);
    } catch {}
    finally { setLoadingCal(false); }
  };

  const laudosFiltrados = laudos.filter(l => {
    const matchBusca = !busca || 
      (l.codigo||'').toLowerCase().includes(busca.toLowerCase()) ||
      (l.tag||'').toLowerCase().includes(busca.toLowerCase()) ||
      (l.razao_social||l.nome_cliente||'').toLowerCase().includes(busca.toLowerCase());
    const matchCliente = !filtroCliente || l.cliente_id === filtroCliente;
    const matchStatus = !filtroStatus || l.status === filtroStatus;
    return matchBusca && matchCliente && matchStatus;
  });

  const colunas = [
    { title: 'Ações', width: 400, render: (_, r) => (r.origem === 'hidrostatico' ? (
      <Space size={2}>
        <Button icon={<EditOutlined />} size="small" type="primary" title="Editar"
          onClick={() => navigate('/testes-hidrostaticos/' + r.id)} />
        {botoesPdf('/testes-hidrostaticos', r, r.codigo)}
        {botoesAnotacao('hidrostatico', r.id)}
        <Button icon={<DeleteOutlined />} size="small" danger title="Excluir"
          onClick={() => excluirHidrostatico(r)} />
      </Space>
    ) : r.origem === 'espessura' ? (
      <Space size={2}>
        <Button icon={<EditOutlined />} size="small" type="primary" title="Editar"
          onClick={() => navigate('/inspecoes-espessura/' + r.id)} />
        {botoesPdf('/inspecoes-espessura', r, r.codigo)}
        {botoesAnotacao('espessura', r.id)}
        <Button icon={<DeleteOutlined />} size="small" danger title="Excluir"
          onClick={() => excluirEspessura(r)} />
      </Space>
    ) : r.origem === 'camara' ? (
      <Space size={2}>
        <Button icon={<EditOutlined />} size="small" type="primary" title="Editar"
          onClick={() => navigate('/inspecoes-camara/' + r.id)} />
        {botoesPdf('/inspecoes-camara', r, r.codigo)}
        {botoesAnotacao('camara', r.id)}
        {(r.tipo_inspecao || 'interna_externa') === 'interna_externa' && (<>
          <Button icon={<BookOutlined />} size="small" style={{color:'#722ed1',borderColor:'#722ed1'}} title="Ver Livro" onClick={() => livroCamara(r, false)} />
          <Button icon={<BookOutlined />} size="small" style={{color:'#722ed1',borderColor:'#722ed1',background:'#f9f0ff'}} title="Gerar Livro" onClick={() => livroCamara(r, true)} />
        </>)}
        <Button icon={<DeleteOutlined />} size="small" danger title="Excluir"
          onClick={() => excluirCamara(r)} />
      </Space>
    ) : r.origem === 'tanque' ? (
      <Space size={2}>
        <Button icon={<EditOutlined />} size="small" type="primary" title="Editar"
          onClick={() => navigate('/inspecoes-tanque/' + r.id)} />
        {botoesPdf('/inspecoes-tanque', r, r.codigo)}
        {botoesAnotacao('tanque', r.id)}
        <Button icon={<DeleteOutlined />} size="small" danger title="Excluir"
          onClick={() => excluirTanque(r)} />
      </Space>
    ) : r.origem === 'tubulacao' ? (
      <Space size={2}>
        <Button icon={<EditOutlined />} size="small" type="primary" title="Editar"
          onClick={() => navigate('/inspecoes-tubulacao/' + r.id)} />
        {botoesPdf('/inspecoes-tubulacao', r, r.codigo)}
        {botoesAnotacao('tubulacao', r.id)}
        <Button icon={<DeleteOutlined />} size="small" danger title="Excluir"
          onClick={() => excluirTubulacao(r)} />
      </Space>
    ) : r.origem === 'externa' ? (
      <Space size={2}>
        <Button icon={<EditOutlined />} size="small" type="primary" title="Editar"
          onClick={() => navigate('/inspecoes-externas/' + r.id)} />
        {botoesPdf('/inspecoes-externas', r, r.codigo)}
        {botoesAnotacao('externa', r.id)}
        <Button icon={<DeleteOutlined />} size="small" danger title="Excluir"
          onClick={() => excluirExterna(r)} />
      </Space>
    ) : (
      <Space size={2}>
        <Button icon={<EditOutlined />} size="small" type="primary" onClick={() => navigate(`/laudos/${r.id}/editar`)} />
        <Button icon={<EyeOutlined />} size="small" style={{color:'#1890ff',borderColor:'#1890ff'}} title="Visualizar PDF" onClick={() => visualizarPDF(r)} />
        <Button icon={<FilePdfOutlined />} size="small" style={{color:'#cf1322',borderColor:'#cf1322'}} title="Gerar novo PDF" onClick={() => gerarPDF(r)} />
        <Button icon={<SafetyCertificateOutlined />} size="small" style={{color:'#52c41a',borderColor:'#52c41a'}} title="Assinar PDF" onClick={() => assinarPDF(r)} />
        {botoesAnotacao('laudo', r.id)}
        {(r.tipo_inspecao_modo || 'interna_externa') === 'interna_externa' && (<>
        <Button icon={<BookOutlined />} size="small" style={{color:'#722ed1',borderColor:'#722ed1'}} title="Ver Livro" onClick={() => visualizarLivro(r)} />
        <Button icon={<BookOutlined />} size="small" style={{color:'#722ed1',borderColor:'#722ed1',background:'#f9f0ff'}} title="Gerar Livro" onClick={() => gerarLivro(r)} />
        <Button icon={<FileProtectOutlined />} size="small" style={{color:'#08979c',borderColor:'#08979c'}} title="Ver Prontuário" onClick={() => visualizarProntuario(r)} />
        <Button icon={<FileProtectOutlined />} size="small" style={{color:'#08979c',borderColor:'#08979c',background:'#e6fffb'}} title="Gerar Prontuário" onClick={() => gerarProntuario(r)} />
        </>)}
        <Button icon={<DeleteOutlined />} size="small" danger onClick={() => excluirLaudo(r)} />
      </Space>
    ))},
    { title: 'Código', dataIndex: 'codigo', width: 110, render: (v) => <Text strong style={{ color: '#1a3c5e' }}>{v}</Text> },
    { title: 'Cliente', dataIndex: 'razao_social', render: (v, r) => <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/clientes/${r.cliente_id}`)}>{v || r.nome_cliente}</Button> },
    { title: 'TAG', dataIndex: 'tag', width: 80, render: (v) => v ? <Tag>{v}</Tag> : '-' },
    { title: 'Tipo de Laudo', dataIndex: 'tipo_laudo', ellipsis: true },
    { title: 'Data Inspeção', dataIndex: 'data_inspecao', width: 110, render: (v) => v ? dayjs(v.substring(0,10)).format('DD/MM/YYYY') : '-' },
    { title: 'Status', dataIndex: 'status', width: 120, render: (v) => <Tag color={STATUS_CORES[v]}>{STATUS_LABELS[v] || v}</Tag> },
    { title: 'Resultado', dataIndex: 'resultado', width: 110, render: (v) => v ? <Tag color={RESULTADO_CORES[v]}>{RESULTADO_LABELS[v] || v}</Tag> : '-' },
    { title: 'Próx. Externa', dataIndex: 'proxima_inspecao_externa', width: 110, render: (v) => statusVencimento(v) },
    { title: 'Próx. Interna', dataIndex: 'proxima_inspecao_interna', width: 110, render: (v) => statusVencimento(v) },
  ];

  const rowSelection = {
    selectedRowKeys: selecionados,
    onChange: (keys) => setSelecionados(keys),
  };

  const calFiltradas = calibracoes.filter(cal => {
    if (!buscaCal) return true;
    const b = buscaCal.toLowerCase();
    return (
      (cal.codigo_cal||'').toLowerCase().includes(b) ||
      (cal.tag||'').toLowerCase().includes(b) ||
      (cal.razao_social||cal.nome_cliente||'').toLowerCase().includes(b) ||
      (cal.laudo_codigo||'').toLowerCase().includes(b)
    );
  });

  const colunasCal = [
    { title: 'Ações', width: 210, render: (_, r) => (
      <Space size={2}>
        <Button icon={<EyeOutlined />} size="small" style={{color:'#1890ff',borderColor:'#1890ff'}} title="Visualizar PDF" onClick={async () => {
          message.loading({ content: 'Carregando PDF...', key: 'calviz', duration: 0 });
          try {
            const rota = r.origem === 'calibracao_avulsa'
              ? '/calibracoes/' + r.id + '/pdf/visualizar'
              : '/laudos/' + r.laudo_id + '/calibracoes/' + r.id + '/pdf/visualizar?origem=' + (r.origem || 'laudo');
            const response = await api.get(rota, { responseType: 'blob' });
            window.open(window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })), '_blank');
            message.destroy('calviz');
          } catch { message.destroy('calviz'); message.warning('PDF ainda não gerado. Use o botão vermelho para gerar.'); }
        }} />
        <Button icon={<FilePdfOutlined />} size="small" style={{color:'#cf1322',borderColor:'#cf1322'}} title="Gerar novo PDF" onClick={async () => {
          message.loading({ content: 'Abrindo PDF...', key: 'calpdf', duration: 0 });
          try {
            const tipo = r.tipo === 'manometro' ? 'manometros' : 'valvulas';
            const rota = r.origem === 'calibracao_avulsa'
              ? '/calibracoes/' + r.id + '/pdf'
              : '/laudos/' + r.laudo_id + '/' + tipo + '/' + r.id + '/pdf?origem=' + (r.origem || 'laudo');
            const response = await api.get(rota, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
            message.destroy('calpdf');
          } catch { message.destroy('calpdf'); message.error('Erro ao gerar PDF'); }
        }} />
        <Button icon={<EditOutlined />} size="small" type="primary" title="Editar laudo"
          onClick={() => navigate(r.origem === 'calibracao_avulsa' ? '/calibracoes/' + r.id
            : r.origem === 'externa' ? '/inspecoes-externas/' + r.laudo_id
            : r.origem === 'camara' ? '/inspecoes-camara/' + r.laudo_id
            : '/laudos/' + r.laudo_id + '/editar')} />
        {r.origem === 'calibracao_avulsa'
          ? botoesAnotacao('calibracao', r.id)
          : botoesAnotacao((['externa', 'camara'].indexOf(r.origem) >= 0 ? r.origem : 'laudo') + '-cal', r.laudo_id)}
        <Popconfirm title="Excluir esta calibração?" description="Não dá para desfazer." okText="Excluir" cancelText="Cancelar" onConfirm={() => excluirCalibracao(r)}>
          <Button icon={<DeleteOutlined />} size="small" danger title="Excluir calibração" />
        </Popconfirm>
      </Space>
    )},
    { title: 'Código', dataIndex: 'codigo_cal', width: 130, render: (v) => <Text strong style={{ color: '#1a3c5e' }}>{v || '-'}</Text> },
    { title: 'Tipo', dataIndex: 'tipo', width: 150, render: (v) => {
      const NOMES = { manometro: 'Manômetro', valvula: 'Válvula', termometro: 'Termômetro',
        calibrador_pneu: 'Calibrador de Pneu', manovacuometro: 'Manovacuômetro', pressostato: 'Pressostato' };
      const CORES = { manometro: 'blue', valvula: 'orange', termometro: 'green', calibrador_pneu: 'purple' };
      return <Tag color={CORES[v] || 'default'}>{NOMES[v] || v}</Tag>;
    } },
    { title: 'TAG', dataIndex: 'tag', width: 80, render: (v) => v ? <Tag>{v}</Tag> : '-' },
    { title: 'Cliente', dataIndex: 'razao_social', render: (v, r) => v || r.nome_cliente },
    { title: 'Laudo de Inspeção', dataIndex: 'laudo_codigo', width: 130, render: (v, r) => (
      r.laudo_id
        ? <Button type="link" style={{ padding: 0 }} onClick={() => navigate(r.origem === 'externa' ? '/inspecoes-externas/' + r.laudo_id
            : r.origem === 'camara' ? '/inspecoes-camara/' + r.laudo_id : `/laudos/${r.laudo_id}`)}>{v || '(sem código)'}</Button>
        : <Tag>avulsa</Tag>
    ) },
    { title: 'Calibrado?', dataIndex: 'calibrado', width: 90, render: (v, r) => (
      r.origem === 'calibracao_avulsa' ? '-' : <Tag color={v || r.calibrada ? 'success' : 'default'}>{v || r.calibrada ? 'Sim' : 'Não'}</Tag>
    ) },
    { title: 'Data Cal.', dataIndex: 'criado_em', width: 100, render: (v) => v ? dayjs(v.substring(0,10)).format('DD/MM/YYYY') : '-' },
    { title: 'Validade', width: 120, render: (_, r) => {
      let venc;
      if (r.data_vencimento) {
        venc = new Date(String(r.data_vencimento).substring(0,10) + 'T12:00:00');
      } else {
        if (!r.data_inspecao || !r.validade_anos) return '-';
        venc = new Date(String(r.data_inspecao).substring(0,10) + 'T12:00:00');
        venc.setFullYear(venc.getFullYear() + parseInt(r.validade_anos));
      }
      const hoje = new Date(new Date().toISOString().substring(0,10));
      const diff = Math.ceil((venc - hoje) / (1000*60*60*24));
      const cor = diff < 0 ? 'red' : diff <= 60 ? 'orange' : 'green';
      return <Tag color={cor}>{venc.toLocaleDateString('pt-BR')}</Tag>;
    }},
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Laudos</Title>
      </div>

      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>Ordenar por:</span>
        <Select value={ordem} onChange={setOrdem} style={{ width: 300 }}
          options={[{ value: 'cod_desc', label: 'Código — mais recente (rascunhos primeiro)' }, 
            { value: 'mod_desc', label: '\u00daltima modifica\u00e7\u00e3o \u2014 mais recente' },
            { value: 'insp_desc', label: 'Data de inspe\u00e7\u00e3o \u2014 mais recente' },
            { value: 'insp_asc', label: 'Data de inspe\u00e7\u00e3o \u2014 mais antiga' },
          ]} />
      </div>
      <Tabs defaultActiveKey="inspecoes">
        <Tabs.TabPane tab={`Inspeções NR-13 (${laudos.length})`} key="inspecoes">
      <Card>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <Input placeholder="Buscar por código, TAG ou cliente..." prefix={<SearchOutlined />} style={{ maxWidth: 300 }} value={busca} onChange={e => setBusca(e.target.value)} />
          <Select allowClear placeholder="Filtrar por cliente" style={{ width: 220 }} value={filtroCliente} onChange={setFiltroCliente} showSearch optionFilterProp="children">
            {clientes.map(c => <Option key={c.id} value={c.id}>{c.razao_social || c.nome}</Option>)}
          </Select>
          <Select allowClear placeholder="Filtrar por status" style={{ width: 160 }} value={filtroStatus} onChange={setFiltroStatus}>
            <Option value="em_elaboracao">Em Elaboração</Option>
            <Option value="concluido">Concluído</Option>
            <Option value="cancelado">Cancelado</Option>
          </Select>
          {selecionados.length > 0 && (
            <Button danger onClick={excluirSelecionados}>Excluir {selecionados.length} selecionados</Button>
          )}
        </div>
        <Table
          rowSelection={rowSelection}
          columns={colunas}
          dataSource={ordenar(laudosFiltrados)}
          loading={loading}
          rowKey={(r) => (r.origem === 'externa' ? 'e' + r.id
            : r.origem === 'tubulacao' ? 't' + r.id
            : r.origem === 'tanque' ? 'q' + r.id
            : r.origem === 'espessura' ? 'm' + r.id
            : r.origem === 'hidrostatico' ? 'h' + r.id
            : r.origem === 'camara' ? 'c' + r.id : r.id)}
          locale={{ emptyText: 'Nenhum laudo cadastrado.' }}
          scroll={{ x: 1300 }}
          size="small"
        />
      </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab={`Calibrações (${calibracoes.length})`} key="calibracoes">
          <Card>
            <Input
              placeholder="Buscar por código, TAG, cliente ou laudo..."
              prefix={<SearchOutlined />}
              style={{ marginBottom: 16, maxWidth: 400 }}
              value={buscaCal}
              onChange={e => setBuscaCal(e.target.value)}
            />
            <Table
              columns={colunasCal}
              dataSource={ordenar(calFiltradas)}
              loading={loadingCal}
              rowKey="id"
              locale={{ emptyText: 'Nenhuma calibração encontrada.' }}
              size="small"
              scroll={{ x: 900 }}
            />
          </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Livros de Registro" key="livros">
          <Card>
            <Table
              columns={[
                { title: 'Ações', width: 130, render: (_, r) => (
                  <Space size={2}>
                    <Button icon={<EyeOutlined />} size="small" style={{color:'#1890ff',borderColor:'#1890ff'}} title="Ver Livro" onClick={() => visualizarLivro(r)} />
                    <Button icon={<BookOutlined />} size="small" style={{color:'#722ed1',borderColor:'#722ed1'}} title="Gerar Livro" onClick={() => gerarLivroDoc(r)} />
                    <Button icon={<EditOutlined />} size="small" type="primary" onClick={() => navigate('/laudos/' + r.id + '/editar')} />
                  </Space>
                )},
                { title: 'Código', dataIndex: 'codigo', render: (v) => <Text strong style={{ color: '#1a3c5e' }}>{v}</Text> },
                { title: 'Cliente', dataIndex: 'razao_social', render: (v, r) => v || r.nome_cliente },
                { title: 'TAG', dataIndex: 'tag', render: (v) => v ? <Tag>{v}</Tag> : '-' },
                { title: 'Data Inspeção', dataIndex: 'data_inspecao', render: (v) => v ? dayjs(v.substring(0,10)).format('DD/MM/YYYY') : '-' },
                { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={STATUS_CORES[v]}>{STATUS_LABELS[v] || v}</Tag> },
              ]}
              dataSource={ordenar(laudos)}
              rowKey="id"
              size="small"
              locale={{ emptyText: 'Nenhum laudo.' }}
              scroll={{ x: 800 }}
            />
          </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab={`Prontuários (${laudos.filter(l => l.prontuario_gerado).length})`} key="prontuarios">
          <Card>
            <Table
              dataSource={ordenar(laudos.filter(l => l.prontuario_gerado))}
              rowKey="id"
              size="small"
              scroll={{ x: 900 }}
              columns={[
                { title: 'Ações', width: 150, render: (_, r) => (
                  <Space size={2}>
                    <Button icon={<EditOutlined />} size="small" type="primary" onClick={() => navigate(`/laudos/${r.id}/editar`)} />
                    <Button icon={<EyeOutlined />} size="small" style={{color:'#1890ff',borderColor:'#1890ff'}} title="Ver Prontuário" onClick={() => visualizarProntuario(r)} />
                    <Button icon={<FileProtectOutlined />} size="small" style={{color:'#08979c',borderColor:'#08979c'}} title="Gerar Prontuário" onClick={() => gerarProntuario(r)} />
                  </Space>
                )},
                { title: 'Código', dataIndex: 'codigo', width: 120, render: (v) => <Tag color="cyan">PRONT-{v}</Tag> },
                { title: 'Cliente', dataIndex: 'razao_social', render: (v, r) => <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/clientes/${r.cliente_id}`)}>{v || r.nome_cliente}</Button> },
                { title: 'TAG', dataIndex: 'tag', width: 80, render: (v) => v ? <Tag>{v}</Tag> : '-' },
                { title: 'Data Inspeção', dataIndex: 'data_inspecao', width: 110, render: (v) => v ? dayjs(v.substring(0,10)).format('DD/MM/YYYY') : '-' },
                { title: 'Técnico', dataIndex: 'tecnico', width: 120 },
              ]}
              locale={{ emptyText: 'Nenhum prontuário gerado ainda.' }}
            />
          </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Estanqueidade" key="estanqueidade">
          <ListaEstanqueidade />
        </Tabs.TabPane>
        <Tabs.TabPane tab={`END (${ensaiosLP.length + espessurasEND.length})`} key="end">
          <Card>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Ensaios não destrutivos. O laudo nasce da OS, na página da ordem de serviço.
            </Text>
            <Table
              rowKey={(r) => (r._end || 'lp') + '_' + r.id}
              size="small"
              loading={loadingLP}
              dataSource={ordenar([
                ...ensaiosLP.map((x) => ({ ...x, _end: 'lp', codigo: x.codigo || x.codigo_end })),
                ...espessurasEND.map((x) => ({ ...x, _end: 'me', codigo_end: x.codigo,
                  equipamento: [x.tipo_equipamento, x.tag].filter(Boolean).join(' — ') || x.equipamento,
                  data_termino: x.data_termino || x.data_inspecao }))])}
              scroll={{ x: 1000 }}
              locale={{ emptyText: 'Nenhum ensaio registrado. Crie a partir de uma OS.' }}
              columns={[
                { title: 'Ações', width: 260, render: (_, r) => r._end === 'me' ? (
                  <Space size={2}>
                    {botoesPdf('/inspecoes-espessura', r, r.codigo)}
                    {botoesAnotacao('espessura', r.id)}
                    <Button icon={<EditOutlined />} size="small" type="primary" title="Editar"
                      onClick={() => navigate('/inspecoes-espessura/' + r.id)} />
                    <Button icon={<DeleteOutlined />} size="small" danger title="Excluir"
                      onClick={() => excluirEspessura(r)} />
                  </Space>
                ) : (
                  <Space size={2}>
                    {botoesPdf('/ensaios-lp', r, r.codigo_end)}
                    {botoesAnotacao('lp', r.id)}
                    <Button icon={<EditOutlined />} size="small" type="primary" title="Editar"
                      onClick={() => navigate('/ensaios-lp/' + r.id)} />
                    <Popconfirm title="Excluir este ensaio?" description="Não dá para desfazer."
                      okText="Excluir" cancelText="Cancelar" onConfirm={() => excluirLP(r)}>
                      <Button icon={<DeleteOutlined />} size="small" danger title="Excluir" />
                    </Popconfirm>
                  </Space>
                )},
                { title: 'Código', dataIndex: 'codigo_end', width: 150,
                  render: (v) => v ? <Text strong style={{ color: '#1a3c5e' }}>{v}</Text> : <Tag>a gerar</Tag> },
                { title: 'Ensaio', width: 150, render: (_, r) => r._end === 'me'
                  ? <Tag color="cyan">Medição de Espessura</Tag> : <Tag color="geekblue">Líquido Penetrante</Tag> },
                { title: 'Cliente', dataIndex: 'razao_social', render: (v, r) => v || r.nome_cliente },
                { title: 'Equipamento', dataIndex: 'equipamento', width: 180, render: (v) => v || '-' },
                { title: 'Seções', width: 150, render: (_, r) => {
                  if (r._end === 'me') return <Text type="secondary">{r.status === 'concluido' ? 'concluído' : 'rascunho'}</Text>;
                  const t = Number(r.qtd_secoes || 0), rp = Number(r.qtd_reprovadas || 0);
                  if (!t) return <Text type="secondary">nenhuma</Text>;
                  return <Space size={4}>
                    <Tag>{t}</Tag>
                    {rp > 0 ? <Tag color="red">{rp} reprovada(s)</Tag> : <Tag color="green">todas aprovadas</Tag>}
                  </Space>;
                } },
                { title: 'OS', dataIndex: 'numero_os', width: 100, render: (v, r) => v || r.os_numero || '-' },
                { title: 'Data de término', dataIndex: 'data_termino', width: 130,
                  render: (v) => v ? dayjs(String(v).substring(0, 10)).format('DD/MM/YYYY') : '-' },
              ]}
            />
          </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab={`Manutenção de Autoclave (${autoclaves.length})`} key="autoclave">
          <Card>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              O laudo nasce da OS, na página da ordem de serviço.
            </Text>
            <Table
              rowKey="id" size="small" dataSource={ordenar(autoclaves)} scroll={{ x: 1000 }}
              locale={{ emptyText: 'Nenhuma manutenção registrada. Crie a partir de uma OS.' }}
              columns={[
                { title: 'Ações', width: 200, render: (_, r) => (
                  <Space size={2}>
                    {botoesPdf('/manutencao-autoclave', r, r.codigo_mpa)}
                    <Button icon={<EditOutlined />} size="small" type="primary" title="Editar"
                      onClick={() => navigate('/manutencao-autoclave/' + r.id)} />
                    <Popconfirm title="Excluir este laudo?" description="Não dá para desfazer."
                      okText="Excluir" cancelText="Cancelar"
                      onConfirm={async () => {
                        try { await api.delete('/manutencao-autoclave/' + r.id); message.success('Excluído'); carregarAutoclaves(); }
                        catch { message.error('Erro ao excluir'); }
                      }}>
                      <Button icon={<DeleteOutlined />} size="small" danger title="Excluir" />
                    </Popconfirm>
                  </Space>
                )},
                { title: 'Documento', dataIndex: 'codigo_mpa', width: 150,
                  render: (v) => v ? <Text strong style={{ color: '#1a3c5e' }}>{v}</Text> : <Tag>a gerar</Tag> },
                { title: 'Cliente', dataIndex: 'razao_social', render: (v, r) => v || r.nome_cliente },
                { title: 'TAG', dataIndex: 'tag', width: 90, render: (v) => v ? <Tag>{v}</Tag> : '-' },
                { title: 'Resultado', dataIndex: 'resultado', width: 160, render: (v) => {
                  const N = { apto: ['Apto', 'success'], nao_apto: ['Não apto', 'error'],
                    apto_com_observacoes: ['Apto c/ obs.', 'warning'] }[v];
                  return N ? <Tag color={N[1]}>{N[0]}</Tag> : '-';
                } },
                { title: 'Data', dataIndex: 'data_manutencao', width: 110,
                  render: (v) => v ? dayjs(String(v).substring(0, 10)).format('DD/MM/YYYY') : '-' },
                { title: 'Vencimento', dataIndex: 'data_vencimento', width: 120,
                  render: (v) => statusVencimento(v) },
              ]}
            />
            <div style={{ display: 'none' }}>
              <Text type="secondary">Módulo de Manutenção de Autoclave em desenvolvimento.</Text>
            </div>
          </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Diversos" key="diversos">
          <ListaDiversos />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
}

export default Laudos;
// Mon Sep 14 11:41:06 AM UTC 2026
